/**
 * The route table (extracted so it is unit-testable). URL paths are ENGLISH code
 * (AX-9); German lives only in nav link TEXT.
 *
 * ROOT DEFECT FIX: a full-path parent record wins over its "" child (documented
 * router precedence), so a bare "/" with only a "" child renders a SILENT EMPTY
 * OUTLET. The fix (web-kit demo pattern): a dedicated redirect record for "/",
 * declared BEFORE the children-bearing "/", plus NO "" child.
 */

import type { RouteRecord, Urn } from "@platform/web-kit"
import type { ShellSession } from "./domain"
import { SETTINGS_GATE_ROLE } from "./domain"

const opportunityUrn = (id: string): Urn => `urn:career:opportunity:${id}`
const applicationUrn = (id: string): Urn => `urn:career:application:${id}`

export function buildRoutes(session: ShellSession): RouteRecord[] {
  const isAuthed = (): boolean => session.user !== null || !session.authEnforced

  const devRoutes: RouteRecord[] = [
    { pattern: "dev/heartbeat", surface: "career-heartbeat" },
    { pattern: "dev/grading", surface: "career-bench-grading" },
    // Bench-lab: one surface for index + detail. The index route sets no urn (index
    // mode); the detail route encodes the results-dir label into a urn the surface
    // reads back — a urn change REBINDS the VM (KitElement §4), so index→detail and
    // between runs both re-fetch cleanly.
    { pattern: "dev/bench-lab", surface: "career-bench-lab" },
    { pattern: "dev/bench-lab/:label", surface: "career-bench-lab", urn: ({ label }): Urn => `urn:career:bench:${label ?? ""}` },
  ]

  const shellChildren: RouteRecord[] = [
    { pattern: "onboarding", surface: "career-onboarding" },
    { pattern: "onboarding/review", surface: "career-extraction-review" },
    { pattern: "workshops", surface: "career-workshop-list" },
    // Werte-Kartenspiel (W1 redesign V2) — a LITERAL segment declared BEFORE the
    // `:slug` param route so it wins (else `:slug` would capture "values" → the
    // runner). Ungated (operator 2026-07-23): available to every authenticated
    // user; the api procedures are auth-scoped and the game is AI-free.
    // The Werte-Interview (W1 redesign V3/V4) — planner + qualitative interview that
    // continues after the pyramid. Declared BEFORE `workshops/values` (deeper literal
    // wins) and the `:slug` param route.
    // The Werte-Sektion (W1 redesign §5b–§8, V5) — the culminating statements pendant.
    // Deepest literal, declared BEFORE the interview + cardgame routes so it wins.
    { pattern: "workshops/values/sektion", surface: "career-werte-sektion", urn: (): Urn => `urn:career:workshop:values` },
    { pattern: "workshops/values/interview", surface: "career-values-interview", urn: (): Urn => `urn:career:workshop:values` },
    { pattern: "workshops/values", surface: "career-values-cardgame", urn: (): Urn => `urn:career:workshop:values` },
    // The runner rebinds on slug change (KitElement urn rebind) — the slug is the
    // English workshop kind (self-knowledge | search-profile | profiler).
    { pattern: "workshops/:slug", surface: "career-workshop-runner", urn: ({ slug }): Urn => `urn:career:workshop:${slug ?? ""}` },
    { pattern: "compass", surface: "career-compass-hub" },
    // »Meine Werte« — the read-only self-view (committed pyramid + confirmed statements).
    { pattern: "compass/werte", surface: "career-werte-view" },
    { pattern: "compass/profile", surface: "career-profile" },
    { pattern: "compass/cv", surface: "career-cv-workbench" },
    { pattern: "compass/evidence", surface: "career-evidence-ledger" },
    { pattern: "compass/assets", surface: "career-assets" },
    { pattern: "search", surface: "career-search" },
    { pattern: "search/opportunities/:id", surface: "career-opportunity-detail", urn: ({ id }): Urn => opportunityUrn(id ?? "") },
    { pattern: "applications", surface: "career-applications" },
    { pattern: "applications/:id", surface: "career-application-review", urn: ({ id }): Urn => applicationUrn(id ?? "") },
    { pattern: "runs", surface: "career-runs" },
    { pattern: "credits", surface: "career-credits" },
    { pattern: "settings/ai", surface: "career-llm-settings" },
    ...(session.devMode ? devRoutes : []),
    { pattern: "*", surface: "career-not-found" }, // in-shell fallback, LAST
  ]

  return [
    { pattern: "/login", surface: "career-login" },
    // Print view — a TOP-LEVEL route (outside the shell) so no nav chrome renders and
    // @page print CSS owns the page. Reads ?version= from the query string.
    { pattern: "/cv-print", surface: "career-cv-print" },
    // Bare "/" → /search. A dedicated leaf record BEFORE the children-bearing "/"
    // (which would otherwise win as a full-path parent and mount an empty outlet).
    { pattern: "/", surface: "career-app", guard: async () => ({ kind: "redirect", url: "/search" }) },
    {
      pattern: "/",
      surface: "career-app",
      guard: async () => (isAuthed() ? { kind: "allow" } : { kind: "redirect", url: "/login" }),
      children: shellChildren,
    },
  ]
}

/** The role that ungates LLM settings — re-exported for the shell nav. */
export { SETTINGS_GATE_ROLE }
